import axios from 'axios';

/**
 * Centralized API Service
 *
 * Features:
 * - Base URL configuration
 * - Automatic auth header injection
 * - Response/Request interceptors
 * - Error handling
 * - Request/Response logging (development only)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE_URL = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Automatically adds auth token to requests
 */
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles common response scenarios
 */
api.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    // Log errors
    console.error('[API Response Error]', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.detail || error.message,
    });

    // Handle specific error codes
    if (error.response?.status === 401) {
      // 401 Unauthorized - Token expired or invalid
      // Note: The AuthContext also handles this, but this is a backup
      console.warn('Unauthorized request - token may be expired');
      // Don't clear token here, let AuthContext handle it to avoid race conditions
    }

    if (error.response?.status === 403) {
      // 403 Forbidden - User doesn't have permission
      console.warn('Forbidden - insufficient permissions');
    }

    if (error.response?.status === 404) {
      // 404 Not Found
      console.warn('Resource not found');
    }

    if (error.response?.status >= 500) {
      // 5xx Server Error
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

/**
 * API Methods
 * Wrapper functions for common HTTP methods
 */

export const apiService = {
  // GET request
  get: (url, config = {}) => api.get(url, config),

  // POST request
  post: (url, data = {}, config = {}) => api.post(url, data, config),

  // PUT request
  put: (url, data = {}, config = {}) => api.put(url, data, config),

  // PATCH request
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),

  // DELETE request
  delete: (url, config = {}) => api.delete(url, config),

  // Upload file
  upload: (url, formData, config = {}) => {
    return api.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

/**
 * Export the axios instance for advanced usage
 */
export default api;

/**
 * Example Usage:
 *
 * // Import the service
 * import { apiService } from '@/services/api';
 *
 * // GET request
 * const response = await apiService.get('/admin/bookings');
 *
 * // POST request
 * const response = await apiService.post('/admin/bookings', bookingData);
 *
 * // With query params
 * const response = await apiService.get('/admin/bookings', {
 *   params: { search: 'query', filter: 'all' }
 * });
 *
 * // With abort controller
 * const controller = new AbortController();
 * const response = await apiService.get('/admin/bookings', {
 *   signal: controller.signal
 * });
 * controller.abort(); // Cancel request
 */
